import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Calendar as CalendarIcon, AirplaneTilt, Clock, Plus, Trash, Bell, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPES = [
  { value: "bloqueo_dia", label: "Bloquear un día", icon: CalendarIcon, hint: "Día concreto en que no estarás disponible." },
  { value: "vacaciones", label: "Solicitar vacaciones", icon: AirplaneTilt, hint: "Rango de fechas. El centro mostrará esos días sin slots disponibles." },
  { value: "cambio_horario", label: "Solicitar cambio de horario", icon: Clock, hint: "Propón un nuevo horario. El admin lo revisará y actualizará la configuración." },
];

const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const TYPE_ICON = Object.fromEntries(TYPES.map((t) => [t.value, t.icon]));

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Availability() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type: "bloqueo_dia",
    date_from: todayIso(),
    date_to: "",
    working_hours_start: "09:00",
    working_hours_end: "20:00",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/availability");
      setItems(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (form.type === "bloqueo_dia") payload.date_to = form.date_from;
      if (form.type !== "cambio_horario") {
        delete payload.working_hours_start;
        delete payload.working_hours_end;
      }
      await api.post("/availability", payload);
      toast.success("Solicitud registrada — el admin ha sido notificado");
      setOpen(false);
      setForm({ ...form, notes: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este registro de disponibilidad?")) return;
    try {
      await api.delete(`/availability/${id}`);
      toast.success("Registro eliminado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const markRead = async (id) => {
    try { await api.put(`/availability/${id}/read`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const markAllRead = async () => {
    try {
      const { data } = await api.post("/availability/read-all");
      toast.success(`${data.marked} aviso(s) marcado(s) como leído(s)`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const unread = items.filter((i) => !i.read_by_admin).length;

  return (
    <div className="space-y-6">
      {/* Sprint B Batch 3 · Panel Gantt diario (solo admin) */}
      {isAdmin && <GanttDayPanel />}

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Disponibilidad</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">
            {isAdmin ? "Avisos del equipo" : "Mi disponibilidad"}
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            {isAdmin
              ? "Bloqueos de día, vacaciones y solicitudes de cambio de horario del equipo."
              : "Avisa al titular cuando bloquees un día, te tomes vacaciones o quieras cambiar tu horario."}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && unread > 0 && (
            <Button variant="outline" className="rounded-sm" onClick={markAllRead} data-testid="mark-all-read-btn">
              <Check className="mr-1.5" /> Marcar todo leído ({unread})
            </Button>
          )}
          {!isAdmin && (
            <Button onClick={()=>setOpen(true)} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="new-availability-btn">
              <Plus className="mr-1.5" /> Nueva solicitud
            </Button>
          )}
        </div>
      </header>

      <div className="bg-white border border-gray-200 rounded-sm">
        {items.length === 0 && (
          <div className="text-center text-gray-500 py-12 text-sm">
            {isAdmin ? "Sin avisos pendientes del equipo." : "Aún no has registrado ninguna solicitud."}
          </div>
        )}
        {items.map((it) => {
          const Icon = TYPE_ICON[it.type] || Bell;
          const isUnread = isAdmin && !it.read_by_admin;
          const rng = it.date_to && it.date_to !== it.date_from ? `${it.date_from} → ${it.date_to}` : it.date_from;
          return (
            <div key={it.id}
              className={`flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-100 last:border-0 ${isUnread ? "bg-amber-50/50" : ""}`}
              data-testid={`availability-${it.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Icon size={20} weight="duotone" className={isUnread ? "text-amber-700" : "text-gray-500 shrink-0"} />
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                    {TYPE_LABEL[it.type] || it.type}
                    {isUnread && <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200 rounded-sm text-[10px] uppercase tracking-wider">Nuevo</Badge>}
                  </div>
                  <div className="text-xs text-gray-500 tabular-nums">
                    {rng}
                    {it.type === "cambio_horario" && it.working_hours_start && (
                      <> · Propuesto: <b>{it.working_hours_start} – {it.working_hours_end}</b></>
                    )}
                    {isAdmin && <> · {it.colaborador_name}</>}
                  </div>
                  {it.notes && <div className="text-xs text-gray-600 mt-1 italic">"{it.notes}"</div>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isAdmin && isUnread && (
                  <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={()=>markRead(it.id)} data-testid={`read-${it.id}`}>
                    <Check size={12} className="mr-1" /> Leído
                  </Button>
                )}
                {(isAdmin || it.colaborador_id === user.id) && (
                  <Button size="icon" variant="ghost" onClick={()=>remove(it.id)} data-testid={`del-${it.id}`}>
                    <Trash size={14} className="text-red-600" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">Nueva solicitud</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo</Label>
              <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="av-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">{TYPES.find((t)=>t.value===form.type)?.hint}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">{form.type === "vacaciones" ? "Desde" : "Fecha"}</Label>
                <Input type="date" value={form.date_from} onChange={(e)=>setForm({...form, date_from: e.target.value})}
                  className="mt-1 rounded-sm" data-testid="av-from" />
              </div>
              {form.type === "vacaciones" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Hasta</Label>
                  <Input type="date" value={form.date_to} onChange={(e)=>setForm({...form, date_to: e.target.value})}
                    className="mt-1 rounded-sm" data-testid="av-to" />
                </div>
              )}
            </div>
            {form.type === "cambio_horario" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Apertura propuesta</Label>
                  <Input type="time" value={form.working_hours_start} onChange={(e)=>setForm({...form, working_hours_start: e.target.value})}
                    className="mt-1 rounded-sm tabular-nums" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Cierre propuesto</Label>
                  <Input type="time" value={form.working_hours_end} onChange={(e)=>setForm({...form, working_hours_end: e.target.value})}
                    className="mt-1 rounded-sm tabular-nums" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider">Notas (opcional)</Label>
              <Input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
                className="mt-1 rounded-sm" placeholder="Motivo, indicaciones…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="av-submit">
              {busy ? "Enviando…" : "Enviar solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// GanttDayPanel · Sprint B Batch 3 · Panel disponibilidad integral del día.
// Lista todos los operarios + sus citas asignadas en bloques horarios.
// ============================================================================
function GanttDayPanel() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments/gantt?date=${d}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const hourStart = parseInt((data?.working_hours?.start || "09:00").split(":")[0], 10);
  const hourEnd = parseInt((data?.working_hours?.end || "20:00").split(":")[0], 10);
  const hours = [];
  for (let h = hourStart; h <= hourEnd; h++) hours.push(h);

  // Unassigned bucket: citas sin operario_id
  const apptsByOp = {};
  (data?.operators || []).forEach((o) => { apptsByOp[o.id] = []; });
  apptsByOp["__unassigned__"] = [];
  (data?.appointments || []).forEach((a) => {
    const opid = a.operario_id || "__unassigned__";
    if (!apptsByOp[opid]) apptsByOp[opid] = [];
    apptsByOp[opid].push(a);
  });

  // Posicionamiento en % del eje horario
  const totalMin = (hourEnd - hourStart) * 60;
  const block = (a) => {
    try {
      const dt = new Date(a.date);
      const startMin = (dt.getHours() - hourStart) * 60 + dt.getMinutes();
      const dur = a.duration_min || 30;
      const leftPct = Math.max(0, (startMin / totalMin) * 100);
      const widthPct = Math.max(2, Math.min(100 - leftPct, (dur / totalMin) * 100));
      return { left: `${leftPct}%`, width: `${widthPct}%` };
    } catch { return { left: "0%", width: "5%" }; }
  };

  return (
    <div className="ag-card p-5" data-testid="gantt-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2"><CalendarIcon size={18} weight="duotone" className="text-[var(--ag-gold)]" />
          <h2 className="font-display text-xl font-bold tracking-tighter">Disponibilidad del día</h2>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)}
            className="h-9 w-44 rounded-sm tabular-nums" data-testid="gantt-date" />
        </div>
      </div>

      {data?.vacation_mode_active && (
        <div className="mb-3 p-3 rounded-sm border border-amber-400 bg-amber-50 text-amber-900 text-xs flex items-center gap-2" data-testid="gantt-vacation-banner">
          <Bell size={14} weight="fill" /> Centro en vacaciones · {data.vacation_message || "Cierre temporal"}
          {data.vacation_until && <span className="opacity-70">· hasta {data.vacation_until}</span>}
        </div>
      )}

      {loading && <div className="text-center py-6 text-sm text-[var(--ag-text-dim)]">Cargando…</div>}

      {!loading && data && (
        <div className="overflow-x-auto -mx-2 px-2" data-testid="gantt-scroll-wrap">
        <div className="min-w-[640px] sm:min-w-0">
          {/* Cabecera horas */}
          <div className="flex gap-2 items-end mb-2">
            <div className="w-32 shrink-0" />
            <div className="flex-1 relative h-5 border-b border-[var(--ag-border)]">
              {hours.map((h) => (
                <div key={h} className="absolute top-0 text-[10px] tabular-nums text-[var(--ag-text-dim)]"
                  style={{ left: `${((h - hourStart) / (hourEnd - hourStart)) * 100}%`, transform: "translateX(-50%)" }}>
                  {String(h).padStart(2, "0")}h
                </div>
              ))}
            </div>
          </div>

          {/* Filas por operario */}
          {(data.operators || []).map((op) => (
            <div key={op.id} className="flex gap-2 items-center mb-2" data-testid={`gantt-row-${op.id}`}>
              <div className="w-32 shrink-0 text-xs truncate">
                <div className="font-semibold">{op.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
                  {op.role === "admin" ? "Titular" : "Colab"} · {apptsByOp[op.id]?.length || 0} citas
                </div>
              </div>
              <div className="flex-1 relative h-8 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm">
                {(apptsByOp[op.id] || []).map((a) => (
                  <div key={a.id} className="absolute top-0.5 bottom-0.5 rounded-sm px-1.5 text-[10px] flex items-center text-[var(--ag-text)] font-medium overflow-hidden whitespace-nowrap"
                    style={{ ...block(a), backgroundColor: a.status === "Pagado" ? "#16a34a" : "#0033A0" }}
                    title={`${a.customer_name} · ${a.wash_type} · ${a.duration_min}min`}
                    data-testid={`gantt-appt-${a.id}`}>
                    {a.license_plate || a.customer_name?.slice(0,12)}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Unassigned */}
          {(apptsByOp["__unassigned__"] || []).length > 0 && (
            <div className="flex gap-2 items-center mb-2" data-testid="gantt-row-unassigned">
              <div className="w-32 shrink-0 text-xs truncate">
                <div className="font-semibold text-amber-600">Sin asignar</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">{apptsByOp["__unassigned__"].length} citas</div>
              </div>
              <div className="flex-1 relative h-8 bg-amber-50/30 border border-amber-300/50 rounded-sm">
                {apptsByOp["__unassigned__"].map((a) => (
                  <div key={a.id} className="absolute top-0.5 bottom-0.5 rounded-sm px-1.5 text-[10px] flex items-center text-white font-medium overflow-hidden whitespace-nowrap bg-amber-500"
                    style={block(a)}
                    title={`${a.customer_name} · ${a.wash_type}`}>
                    {a.license_plate || a.customer_name?.slice(0,12)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(data.operators || []).length === 0 && (data.appointments || []).length === 0 && (
            <div className="text-center py-6 text-sm text-[var(--ag-text-dim)]">Sin citas para este día.</div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}

